import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';

interface Feature {
    _id?: string;
    icon: string;
    image?: string;
    hasInnerCheck: boolean;
    title: string;
    desc: string;
    pillIcon: string;
    theme: string;
}

const AdminHomepageSettings: React.FC = () => {
    const { token } = useAuth();
    const [whyTitle, setWhyTitle] = useState('WHY CHOOSE US');
    const [whySubtitle, setWhySubtitle] = useState('Your Trust is Our Priority');
    const [whyFeatures, setWhyFeatures] = useState<Feature[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data && !data.message) {
                    setWhyTitle(data.whyTitle || 'WHY CHOOSE US');
                    setWhySubtitle(data.whySubtitle || 'Your Trust is Our Priority');
                    setWhyFeatures(data.whyFeatures || []);
                }
            } catch (err) {
                console.error('Failed to fetch settings:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleAddFeature = () => {
        setWhyFeatures([...whyFeatures, {
            icon: 'fa-star',
            image: '',
            hasInnerCheck: false,
            title: 'New Feature',
            desc: 'Description here',
            pillIcon: 'fa-check',
            theme: 'blue'
        }]);
    };

    const handleRemoveFeature = (index: number) => {
        const newFeatures = [...whyFeatures];
        newFeatures.splice(index, 1);
        setWhyFeatures(newFeatures);
    };

    const handleFeatureChange = (index: number, field: keyof Feature, value: any) => {
        const newFeatures = [...whyFeatures];
        newFeatures[index] = { ...newFeatures[index], [field]: value };
        setWhyFeatures(newFeatures);
    };

    const handleFeatureImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const file = e.target.files?.[0];
        if (!file || !token) return;

        const formData = new FormData();
        formData.append('images', file);

        try {
            const res = await fetch(`${(window as any).API_BASE || 'https://mypropertyguru.in/api'}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (data.urls && data.urls.length > 0) {
                handleFeatureChange(index, 'image', data.urls[0]);
                toast.success('Feature image uploaded!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Failed to upload image');
        }
    };

    const handleSave = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const data = await api.updateSystemSettings(token, {
                whyTitle,
                whySubtitle,
                whyFeatures
            });
            if (data && !data.message) {
                toast.success('Homepage settings updated successfully');
            } else {
                toast.error(data.message || 'Failed to update settings');
            }
        } catch (err) {
            console.error('Update failed:', err);
            toast.error('An error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h3 className="text-3xl font-bold text-slate-900 dark:text-white leading-none">
                        Homepage <span className="text-[#05144b]">Settings</span>
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm mt-3">
                        Manage the "Why Choose Us" section and other homepage content.
                    </p>
                </div>
                <button 
                    onClick={handleSave}
                    disabled={saving}
                    className="px-8 py-3.5 bg-[#05144b] text-white rounded-xl font-bold hover:bg-blue-900 transition-all shadow-lg flex items-center gap-2"
                >
                    {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <i className="fa-solid fa-floppy-disk"></i>}
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 shadow-sm">
                    <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-400 font-medium">Loading settings...</p>
                </div>
            ) : (
                <div className="max-w-5xl mx-auto space-y-8 pb-20">
                    {/* Header Settings */}
                    <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-100 shadow-xl">
                        <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                            <i className="fa-solid fa-heading text-blue-500"></i> Section Header
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Main Title</label>
                                <input 
                                    type="text" 
                                    value={whyTitle}
                                    onChange={(e) => setWhyTitle(e.target.value)}
                                    className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#05144b]/20 focus:border-[#05144b] outline-none transition-all font-bold text-slate-800"
                                    placeholder="e.g. WHY CHOOSE US"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Trust Text (Animated)</label>
                                <input 
                                    type="text" 
                                    value={whySubtitle}
                                    onChange={(e) => setWhySubtitle(e.target.value)}
                                    className="w-full px-5 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#05144b]/20 focus:border-[#05144b] outline-none transition-all font-bold text-slate-800"
                                    placeholder="e.g. Your Trust is Our Priority"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Features Settings */}
                    <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-100 shadow-xl">
                        <div className="flex items-center justify-between mb-8">
                            <h4 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                <i className="fa-solid fa-list-check text-orange-500"></i> Features List
                            </h4>
                            <button 
                                onClick={handleAddFeature}
                                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-bold hover:bg-blue-100 transition-all flex items-center gap-2"
                            >
                                <i className="fa-solid fa-plus"></i> Add Feature
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {whyFeatures.map((feature, idx) => (
                                <div key={idx} className="p-6 rounded-2xl bg-slate-50 border border-slate-200 relative group">
                                    <button 
                                        onClick={() => handleRemoveFeature(idx)}
                                        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-rose-50 text-rose-500 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center hover:bg-rose-100"
                                    >
                                        <i className="fa-solid fa-trash-can text-sm"></i>
                                    </button>
                                    
                                    <div className="grid grid-cols-1 gap-4">
                                        <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200">
                                            <div className="w-16 h-16 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-200 relative group/img">
                                                {feature.image ? (
                                                    <>
                                                        <img src={getImageUrl(feature.image)} alt="Feature" className="w-full h-full object-contain" />
                                                        <button 
                                                            onClick={() => handleFeatureChange(idx, 'image', '')}
                                                            className="absolute inset-0 bg-rose-500/80 text-white opacity-0 group-hover/img:opacity-100 transition-all flex items-center justify-center text-xs font-bold"
                                                        >
                                                            Remove
                                                        </button>
                                                    </>
                                                ) : (
                                                    <i className={`fa-solid ${feature.icon} text-slate-300 text-2xl`}></i>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <label htmlFor={`feature-img-${idx}`} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-[10px] font-black uppercase tracking-wider cursor-pointer hover:bg-blue-100 transition-all">
                                                    Upload Image
                                                </label>
                                                <p className="text-[10px] text-slate-400 mt-1 font-medium italic">Replaces Icon if uploaded</p>
                                                <input type="file" id={`feature-img-${idx}`} className="hidden" accept="image/*" onChange={(e) => handleFeatureImageUpload(e, idx)} />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Icon (Fallback)</label>
                                                <input 
                                                    type="text" 
                                                    value={feature.icon}
                                                    onChange={(e) => handleFeatureChange(idx, 'icon', e.target.value)}
                                                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700"
                                                    placeholder="fa-star"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Theme</label>
                                                <select 
                                                    value={feature.theme}
                                                    onChange={(e) => handleFeatureChange(idx, 'theme', e.target.value)}
                                                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700"
                                                >
                                                    <option value="blue">Blue</option>
                                                    <option value="orange">Orange</option>
                                                    <option value="green">Green</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Title (Use \n for new line)</label>
                                            <input 
                                                type="text" 
                                                value={feature.title}
                                                onChange={(e) => handleFeatureChange(idx, 'title', e.target.value)}
                                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700"
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Description</label>
                                            <input 
                                                type="text" 
                                                value={feature.desc}
                                                onChange={(e) => handleFeatureChange(idx, 'desc', e.target.value)}
                                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Pill Icon</label>
                                                <input 
                                                    type="text" 
                                                    value={feature.pillIcon}
                                                    onChange={(e) => handleFeatureChange(idx, 'pillIcon', e.target.value)}
                                                    className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700"
                                                />
                                            </div>
                                            <div className="flex items-center gap-3 pt-6">
                                                <input 
                                                    type="checkbox" 
                                                    checked={feature.hasInnerCheck}
                                                    onChange={(e) => handleFeatureChange(idx, 'hasInnerCheck', e.target.checked)}
                                                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-xs font-bold text-slate-600">Inner Check?</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminHomepageSettings;
